'use client'

import { useEffect, useMemo, useRef, useState } from 'react'
import { calcularPrecificacao, type LucroTipo } from '@/lib/calculos/precificacao'
import {
  AvisoBox,
  brl,
  DetailRow,
  LeadCapture,
  SalvarCalculoCta,
  Eyebrow,
  Icon,
  MobileResultBar,
  MoneyField,
  parseValor,
  ResultCard,
  RowStrong,
  sanitizeMoneyInput,
  SoftExportBar,
  useAnimatedNumber,
  useCalcTelemetry,
} from '@/components/calc'
import type { CartaoLinha, CartaoPayload } from '@/lib/export/types'
import { cn } from '@/lib/utils'

type Tipo = 'produto' | 'servico' | 'ambos'

const TIPOS: { id: Tipo; label: string; icon: 'tag' | 'clock' | 'scale' }[] = [
  { id: 'produto', label: 'Um produto', icon: 'tag' },
  { id: 'servico', label: 'Um serviço', icon: 'clock' },
  { id: 'ambos', label: 'Os dois', icon: 'scale' },
]

export function PrecoVendaClient({ mobileBar = true }: { mobileBar?: boolean } = {}) {
  const [tipo, setTipo] = useState<Tipo>('ambos')
  const [custoMaterial, setCustoMaterial] = useState('')
  const [custoMaoObra, setCustoMaoObra] = useState('')
  const [outrosCustos, setOutrosCustos] = useState('')
  const [lucroTipo, setLucroTipo] = useState<LucroTipo>('percentual')
  const [lucroReais, setLucroReais] = useState('')
  const [lucroPct, setLucroPct] = useState('30')

  // Pré-preenche com o que veio do console da home (?custo=&margem=) — sem redigitar.
  useEffect(() => {
    const p = new URLSearchParams(window.location.search)
    const custo = p.get('custo')
    const margem = p.get('margem')
    if (custo) setCustoMaterial(custo)
    if (margem) setLucroPct(margem)
  }, [])

  const incluiProduto = tipo === 'produto' || tipo === 'ambos'
  const incluiServico = tipo === 'servico' || tipo === 'ambos'

  const r = useMemo(
    () =>
      calcularPrecificacao({
        custoMaterial: incluiProduto ? parseValor(custoMaterial) : 0,
        custoMaoObra: incluiServico ? parseValor(custoMaoObra) : 0,
        outrosCustos: parseValor(outrosCustos),
        lucroTipo,
        lucroValor: lucroTipo === 'reais' ? parseValor(lucroReais) : parseValor(lucroPct),
      }),
    [incluiProduto, incluiServico, custoMaterial, custoMaoObra, outrosCustos, lucroTipo, lucroReais, lucroPct]
  )

  const heroAnim = useAnimatedNumber(r.precoVenda)

  useCalcTelemetry(
    'precificacao',
    (incluiProduto && parseValor(custoMaterial) > 0) ||
      (incluiServico && parseValor(custoMaoObra) > 0),
    `Cobre ${brl(r.precoVenda)} · lucro ${brl(r.lucro)}`,
  )

  const buildCartao = (publico: 'cliente' | 'eu' | null): CartaoPayload => {
    const custos: CartaoLinha[] = [
      ...(incluiProduto && parseValor(custoMaterial) > 0
        ? [{ label: 'Material', value: brl(parseValor(custoMaterial)) }]
        : []),
      ...(incluiServico && parseValor(custoMaoObra) > 0
        ? [{ label: 'Mão de obra', value: brl(parseValor(custoMaoObra)) }]
        : []),
      ...(parseValor(outrosCustos) > 0
        ? [{ label: 'Outros custos', value: brl(parseValor(outrosCustos)) }]
        : []),
    ]
    const linhas: CartaoLinha[] =
      publico === 'cliente'
        ? [...custos, { label: 'Total', value: brl(r.precoVenda), strong: true, tone: 'ink' }]
        : [
            ...custos,
            { label: 'Custo total', value: brl(r.custoTotal) },
            { label: 'Seu lucro', value: `+ ${brl(r.lucro)}`, strong: true, tone: 'brand' },
          ]
    return {
      nome: 'preco-de-venda',
      heroLabel: publico === 'cliente' ? 'Valor do serviço' : 'Preço de venda',
      heroValue: brl(r.precoVenda),
      tone: 'brand',
      linhas,
    }
  }

  const cardRef = useRef<HTMLElement>(null)

  return (
    <div className="grid grid-cols-1 gap-[18px] pb-24 lg:grid-cols-2 lg:items-start lg:pb-0">
      {/* ── INPUTS ── */}
      <section className="rounded-2xl border border-line bg-surface p-6 shadow-soft">
        {/* o que vende */}
        <div className="mb-4 text-sm text-ink">O que você vende?</div>
        <div className="mb-5 grid grid-cols-3 gap-2">
          {TIPOS.map((t) => {
            const active = tipo === t.id
            return (
              <button
                key={t.id}
                type="button"
                onClick={() => setTipo(t.id)}
                className={cn(
                  'flex flex-col items-center gap-1.5 rounded-[10px] border px-2 py-3 text-[13px] font-medium',
                  active
                    ? 'border-brand bg-brand-tint text-brand'
                    : 'border-line bg-surface text-ink-mute hover:border-brand'
                )}
              >
                <Icon name={t.icon} size={18} />
                {t.label}
              </button>
            )
          })}
        </div>

        {incluiProduto && (
          <MoneyField
            label="Quanto custou o material / produto?"
            value={custoMaterial}
            onChange={setCustoMaterial}
          />
        )}
        {incluiServico && (
          <MoneyField
            label="Quanto vale o seu trabalho nisso?"
            value={custoMaoObra}
            onChange={setCustoMaoObra}
          />
        )}
        <MoneyField
          label="Outros custos (opcional)"
          value={outrosCustos}
          onChange={setOutrosCustos}
          hint="Embalagem, frete, taxa da maquininha…"
        />

        {/* lucro */}
        <div className="mt-2">
          <div className="mb-2 flex items-center justify-between">
            <span className="text-sm text-ink">Quanto você quer ganhar?</span>
            <div className="inline-flex overflow-hidden rounded-[10px] border border-line">
              <LucroTab active={lucroTipo === 'reais'} onClick={() => setLucroTipo('reais')}>
                R$
              </LucroTab>
              <LucroTab active={lucroTipo === 'percentual'} onClick={() => setLucroTipo('percentual')}>
                %
              </LucroTab>
            </div>
          </div>
          {lucroTipo === 'reais' ? (
            <div className="flex h-[46px] items-center overflow-hidden rounded-[10px] border border-line bg-surface focus-within:ring-1 focus-within:ring-inset focus-within:ring-brand">
              <span className="flex h-full items-center border-r border-line-soft px-3 font-mono text-sm text-ink-mute">
                R$
              </span>
              <input
                inputMode="decimal"
                value={lucroReais}
                placeholder="0,00"
                onChange={(e) => setLucroReais(sanitizeMoneyInput(e.target.value))}
                className="h-full w-full flex-1 bg-transparent px-3 font-mono text-base text-ink outline-none placeholder:text-ink-faint"
              />
            </div>
          ) : (
            <div className="flex h-[46px] items-center overflow-hidden rounded-[10px] border border-line bg-surface focus-within:ring-1 focus-within:ring-inset focus-within:ring-brand">
              <input
                inputMode="decimal"
                value={lucroPct}
                placeholder="0"
                onChange={(e) => setLucroPct(sanitizeMoneyInput(e.target.value))}
                className="h-full w-full flex-1 bg-transparent px-3 font-mono text-base text-ink outline-none placeholder:text-ink-faint"
              />
              <span className="flex h-full items-center border-l border-line-soft px-3 font-mono text-sm text-ink-mute">
                % do preço
              </span>
            </div>
          )}
        </div>
      </section>

      {/* ── RESULTADO ── */}
      <section ref={cardRef} className="flex flex-col rounded-2xl border border-line bg-bg-alt p-[22px] shadow-soft">
        <Eyebrow icon="tag" tone="muted" className="mb-3">
          O preço de venda
        </Eyebrow>

        <ResultCard
          heroLabel="Cobre do seu cliente"
          heroValue={r.calculavel ? brl(heroAnim) : '—'}
        >
          {!r.calculavel ? (
            <p className="py-2 text-[13px] text-ink-mute">
              Informe pelo menos um custo pra calcular o preço.
            </p>
          ) : (
            <>
              {incluiProduto && parseValor(custoMaterial) > 0 && (
                <DetailRow label="Material" value={brl(parseValor(custoMaterial))} />
              )}
              {incluiServico && parseValor(custoMaoObra) > 0 && (
                <DetailRow label="Mão de obra" value={brl(parseValor(custoMaoObra))} />
              )}
              {parseValor(outrosCustos) > 0 && (
                <DetailRow label="Outros custos" value={brl(parseValor(outrosCustos))} />
              )}
              <DetailRow label="Custo total" value={brl(r.custoTotal)} />
              <RowStrong label="Seu lucro" value={`+ ${brl(r.lucro)}`} tone="brand" />
              <p className="mt-1.5 font-mono text-[11.5px] text-ink-faint">
                margem de {r.margemSobreVenda.toLocaleString('pt-BR', { maximumFractionDigits: 0 })}% do preço
              </p>
            </>
          )}
        </ResultCard>

        <AvisoBox tone="brand" icon="eye-off" className="mt-4">
          Esse cartão mostra o seu lucro. Na hora de exportar, você escolhe escondê-lo pra mandar
          pro cliente — o padrão já sai sem o lucro.
        </AvisoBox>

        <SoftExportBar buildCartao={() => buildCartao('eu')} />
        <LeadCapture show={r.calculavel} source="preco-de-venda" alertType="das" />
        <SalvarCalculoCta
          show={r.calculavel}
          source="preco-de-venda"
          hook="Preço fechado. No Premium você registra as vendas e vê o orçamento."
          calcData={
            r.calculavel
              ? {
                  tipo: 'PRECIFICACAO',
                  inputs: {
                    custoProduto: incluiProduto ? parseValor(custoMaterial) : 0,
                    custosFixosRateados: parseValor(outrosCustos),
                    despesasVariaveis: incluiServico ? parseValor(custoMaoObra) : 0,
                    margemDesejada: Math.min(
                      99,
                      Math.max(
                        0,
                        lucroTipo === 'percentual'
                          ? parseValor(lucroPct)
                          : r.precoVenda > 0
                            ? (r.lucro / r.precoVenda) * 100
                            : 0,
                      ),
                    ),
                  },
                  resultado: {
                    custoTotal: r.custoTotal,
                    precoVenda: r.precoVenda,
                    markup: r.custoTotal > 0 ? (r.lucro / r.custoTotal) * 100 : 0,
                    lucro: r.lucro,
                  },
                  titulo: 'Preço de venda',
                }
              : undefined
          }
        />
      </section>

      {mobileBar && r.calculavel && <MobileResultBar payload={buildCartao('eu')} cardRef={cardRef} />}
    </div>
  )
}

function LucroTab({
  active,
  onClick,
  children,
}: {
  active: boolean
  onClick: () => void
  children: React.ReactNode
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        'px-3.5 py-1.5 font-mono text-[13px]',
        active ? 'bg-brand text-white' : 'bg-surface text-ink-mute hover:text-ink'
      )}
    >
      {children}
    </button>
  )
}
